/**
 * ADD SHOPPING AD GROUPS — /google-ads/shopping/create/adgroup[?campaign_id=…]
 * Each new group gets its "All products" product group and (optionally) a
 * product ad, atomically. Resumable: created groups are remembered by draft id.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Select } from '@/components/Select';
import { googleAdsService, GoogleAdsCampaignSummary, ShoppingCampaignMeta } from '@/services/google-ads.service';
import { BackTo, biddingLabel, errMsg, formatMicros, Pill, Section, statusOf, Tag, useYoutubeAuth } from '@/components/google-ads/youtube/youtubeShared';
import { Pub, toMicros } from '@/components/google-ads/objectives/objectiveShared';
import { ConnectFirst, GoalStrip, PageSpinner, SaveBar } from '@/components/google-ads/objectives/wizardChrome';
import {
  cloneShoppingGroup, isManualCpc, isShoppingCampaign, newShoppingGroup, priorityLabel, ShoppingGroupCard, ShoppingGroupDraft, shoppingGroupIssues,
  ShoppingPreview,
} from '@/components/google-ads/shopping/shoppingShared';
import { SHOPPING_OBJECTIVE as CFG } from './shoppingObjective';

export default function CreateShoppingAdgroup() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { auth, accountUid } = useYoutubeAuth();
  const ready = !!(auth?.connected && auth?.customer_selected && accountUid);
  const currency = auth?.currency ?? '';

  const [campaigns, setCampaigns] = useState<GoogleAdsCampaignSummary[] | null>(null);
  const [meta, setMeta] = useState<Record<string, ShoppingCampaignMeta>>({});
  const [campaignId, setCampaignId] = useState(params.get('campaign_id') || '');
  const campaign = campaigns?.find((c) => c.id === campaignId);
  const m = campaign ? meta[campaign.id] : undefined;
  const manualCpc = isManualCpc(campaign?.biddingStrategyType);

  const first = useMemo(() => newShoppingGroup(1), []);
  const [groups, setGroups] = useState<ShoppingGroupDraft[]>([first]);
  const [openId, setOpenId] = useState(first.id);
  const patch = (gid: string, p: Partial<ShoppingGroupDraft>) => setGroups((gs) => gs.map((g) => (g.id === gid ? { ...g, ...p } : g)));

  const [pub, setPub] = useState<Record<string, Pub>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const madeRef = useRef<Record<string, string>>({});

  useEffect(() => {
    if (!accountUid) return;
    Promise.all([
      googleAdsService.listCampaigns(accountUid),
      googleAdsService.shopping.campaignMeta(accountUid).catch(() => ({} as Record<string, ShoppingCampaignMeta>)),
    ]).then(([list, mm]) => { setCampaigns(list.filter((c) => isShoppingCampaign(c) && c.status !== 'REMOVED')); setMeta(mm); })
      .catch(() => setCampaigns([]));
  }, [accountUid]);

  const issues = useMemo(() => {
    const i: string[] = [];
    if (!campaignId) i.push('Pick the campaign these ad groups belong to.');
    groups.forEach((g, n) => { const gi = shoppingGroupIssues(g, manualCpc); if (gi.length) i.push(`${g.name.trim() || `Ad group ${n + 1}`}: needs ${gi.join(', ')}.`); });
    return i;
  }, [campaignId, groups, manualCpc]);

  const submit = async () => {
    if (!accountUid || issues.length) return;
    setSaving(true); setError(null);
    const mark = (k: string, v: Pub) => setPub((x) => ({ ...x, [k]: v }));
    try {
      for (const g of groups) {
        if (madeRef.current[g.id]) continue;
        mark(g.id, 'running');
        try {
          const r = await googleAdsService.shopping.createAdGroup(accountUid, {
            campaignId, name: g.name.trim(), status: 'PAUSED', cpcBidMicros: manualCpc ? toMicros(g.cpcBid) : undefined, withProductAd: g.withAd,
          });
          madeRef.current[g.id] = r.adGroupId;
        } catch (e) { mark(g.id, 'failed'); setError(`${g.name.trim()}: ${errMsg(e)}`); return; }
        mark(g.id, 'done');
      }
      toast.success(groups.length === 1 ? 'Ad group created — paused until you enable it.' : `${groups.length} ad groups created — paused until you enable them.`);
      navigate(CFG.base);
    } finally { setSaving(false); }
  };

  const st = campaign ? statusOf(campaign.status, campaign.primaryStatus) : null;

  return (
    <>
      <Topbar title="New shopping ad groups" breadcrumb={`Google Ads / ${CFG.title}`} description="Each group gets an “All products” product group and a product ad"
        back={<BackTo to={CFG.base} label={`Back to ${CFG.title}`} />} />

      <div className="w-full px-7 py-7">
        {auth === undefined && <PageSpinner />}
        {auth !== undefined && !ready && <ConnectFirst what="ad groups" />}

        {ready && (
          <div className="flex w-full gap-5 pb-12">
            <div className="min-w-0 flex-1 space-y-4">
              <GoalStrip items={[
                { label: 'Goal', value: CFG.goal },
                { label: 'Type', value: 'Shopping ad groups' },
                { label: 'Adding to', value: campaign?.name ?? 'pick a campaign' },
              ]}>
                <Tag>starts paused</Tag>
              </GoalStrip>

              <Section title="Campaign" hint="The Shopping campaign these ad groups belong to.">
                <Select className="h-10 rounded-lg" value={campaignId} onChange={(e) => setCampaignId(e.target.value)} disabled={Object.keys(madeRef.current).length > 0}>
                  <option value="">{campaigns === null ? 'Loading campaigns…' : campaigns.length ? 'Pick a Shopping campaign' : 'No Shopping campaigns yet — create one first'}</option>
                  {(campaigns ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </Select>
                {campaign && st && (
                  <div className="mt-3 flex flex-wrap items-center gap-2 text-2xs text-text-muted">
                    <Pill {...st} />
                    {biddingLabel(campaign.biddingStrategyType) && <Tag>{biddingLabel(campaign.biddingStrategyType)}</Tag>}
                    {formatMicros(campaign.budgetAmountMicros, currency) && <Tag>{formatMicros(campaign.budgetAmountMicros, currency)} / day</Tag>}
                    {m?.merchantId && <Tag>Merchant Center {m.merchantId}</Tag>}
                    {m && <Tag>{priorityLabel(m.campaignPriority)} priority</Tag>}
                    {manualCpc && <span className="text-text-secondary">· Manual CPC — each new group needs a max CPC</span>}
                  </div>
                )}
              </Section>

              {groups.map((g, n) => (
                <ShoppingGroupCard key={g.id} group={g} index={n} open={openId === g.id} onToggle={() => setOpenId(openId === g.id ? '' : g.id)}
                  manualCpc={manualCpc} currency={currency} state={pub[g.id]} made={!!madeRef.current[g.id]} busy={saving} onChange={(p) => patch(g.id, p)}
                  onDuplicate={() => { const c = cloneShoppingGroup(g); setGroups((gs) => { const i = gs.findIndex((x) => x.id === g.id); return [...gs.slice(0, i + 1), c, ...gs.slice(i + 1)]; }); setOpenId(c.id); }}
                  onRemove={groups.length > 1 ? () => setGroups((gs) => gs.filter((x) => x.id !== g.id)) : undefined} />
              ))}
              <Button variant="secondary" className="w-full" iconLeft={<Plus size={15} />} disabled={saving}
                onClick={() => { const g = newShoppingGroup(groups.length + 1); setGroups((gs) => [...gs, g]); setOpenId(g.id); }}>Add another ad group</Button>

              <SaveBar issues={issues} error={error} saving={saving} onSave={submit} onCancel={() => navigate(CFG.base)}
                label={groups.length === 1 ? 'Create ad group' : `Create ${groups.length} ad groups`} />
            </div>

            <div className="hidden w-[320px] shrink-0 xl:block">
              <div className="sticky top-24">
                <ShoppingPreview merchantId={m?.merchantId ?? ''} merchantLinked={m?.merchantId ? true : null} groups={groups.length}
                  withAds={groups.filter((g) => g.withAd).length} local={false} feedLabel={m?.feedLabel} currency={currency} />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
